var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import get from 'lodash/get';
import { useEffect, useMemo } from 'react';
import { useGetList, useGetMany, useGetManyReference, useRecordContext, useResourceContext, } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import { UsingRegexp } from './constants';
import getReferenceManyToManyFormField from './getReferenceManyToManyFormField';
import { useReferenceParams } from './useReferenceParams';
import { useRegisterManyToManyHandling } from './useRegisterManyToManyHandling';
/**
 * Hook that fetches records from another resource in a many to many scenario implemented with an associative resource.
 *
 * @example
 *
 * const { ids, data, error, loaded, loading, total } = useReferenceManyToManyInputController({
 *      record: { id: 1, name: 'Eric Clapton' },
 *      reference: 'events',
 *      resource: 'artists',
 *      sort: { field: 'name', order: 'ASC' },
 *      source: 'id',
 *      through: 'performances',
 *      using: 'artist_id,event_id'
 * });
 *
 * @param {Object} options
 * @param {boolean} options.filter Filters to send with the request
 * @param {string} options.reference The linked resource name
 * @param {string} options.resource The current resource name
 * @param {Object} options.sort The sort parameters
 * @param {string} options.sort.field The field used for sorting
 * @param {string} options.sort.order The sort order (asc, desc)
 * @param {string} options.source The key of the linked resource identifier
 * @param {string} options.through The name of the associative resource
 * @param {string} options.using A comma separated list of two field names which are the ids of the two linked resource in the associative resource
 * @param {number} options.perPage Optional. The number of events to fetch. Defaults to 25
 *
 * @returns {ReferenceArrayProps} The reference props
 */
export var useReferenceManyToManyInputController = function (options) {
    var debounce = options.debounce, filter = options.filter, _a = options.filterChoices, initialFilterChoices = _a === void 0 ? options.filterEndResource : _a, _b = options.perPage, initialPerPage = _b === void 0 ? 25 : _b, _c = options.perPageChoices, initialPerPageChoices = _c === void 0 ? options.perPageEndResource ||
        25 : _c, reference = options.reference, initialSort = options.sort, _d = options.sortChoices, initialSortChoices = _d === void 0 ? options.sortEndResource : _d, through = options.through, using = options.using, _e = options.source, source = _e === void 0 ? 'id' : _e;
    if (options.perPageEndResource != null &&
        process.env.NODE_ENV === 'development') {
        console.warn("The perPageEndResource prop is deprecated. You should provide the perPageChoices prop instead.");
    }
    if (options.sortEndResource != null &&
        process.env.NODE_ENV === 'development') {
        console.warn("The sortEndResource prop is deprecated. You should provide the sortChoices prop instead.");
    }
    if (options.filterEndResource != null &&
        process.env.NODE_ENV === 'development') {
        console.warn("The filterEndResource prop is deprecated. You should provide the filterChoices prop instead.");
    }
    var resource = useResourceContext(options);
    useRegisterManyToManyHandling({
        reference: reference,
        resource: resource,
        through: through,
        using: using,
        perPage: initialPerPage,
    });
    if (!using.match(UsingRegexp)) {
        throw new Error('useReferenceManyToManyInputController incorrect `using` option format. `using` should be a string of two fields separated by a comma  such as `book_id,author_id`');
    }
    var record = useRecordContext(options);
    var _f = using.match(UsingRegexp), sourceField = _f[1], targetField = _f[2];
    // As the edited record does not contains the references in a many-to-many
    // relationship, we have to store them in a temporary field in the current form,
    // named according to the many-to-many configuration.
    // This field will be removed from the form values before the final submit
    var temporaryFieldName = getReferenceManyToManyFormField({
        reference: reference,
        resource: resource,
        through: through,
    });
    var _g = useFormContext(), resetField = _g.resetField, formState = _g.formState;
    var params = useReferenceParams({
        resource: reference,
        // TODO: Introduce real pagination (setters, etc.)
        page: 1,
        perPage: initialPerPage,
        sort: initialSort,
        debounce: debounce,
        filter: filter,
    })[0];
    var throughManyReferences = useGetManyReference(through, {
        id: get(record, source),
        target: sourceField,
        pagination: { page: params.page, perPage: params.perPage },
        sort: {
            field: params.sort,
            order: params.order,
        },
        filter: params.filter,
    }, {
        enabled: !!(record === null || record === void 0 ? void 0 : record.id),
    });
    var throughManyReferencesIds = throughManyReferences.data
        ? throughManyReferences.data.map(function (record) { return record.id; })
        : [];
    var referencesIds = throughManyReferencesIds.reduce(function (acc, id) {
        var record = throughManyReferences.data.find(function (item) { return item.id === id; });
        if (record) {
            if (acc.indexOf(record[targetField]) === -1) {
                acc.push(record[targetField]);
            }
        }
        return acc;
    }, []);
    var referenceIdsEffectSignature = referencesIds.join(',');
    // This hook is used to reset the temporary field's default value whenever the references ids
    // (fetched from the api) change, and also re-apply the value when the form is submitted
    // (because the temporary field is removed from the form values before the final submit)
    useEffect(function () {
        setTimeout(function () {
            if (
            // This ensure we only resets for existing records. Otherwise it would prevent defaultValue on children
            !!(record === null || record === void 0 ? void 0 : record.id) &&
                !isEqual(referencesIds, formState.defaultValues
                    ? formState.defaultValues[temporaryFieldName]
                    : [])) {
                resetField(temporaryFieldName, {
                    defaultValue: referencesIds,
                });
            }
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        referenceIdsEffectSignature,
        temporaryFieldName,
        resetField,
        formState.submitCount,
        formState.defaultValues,
    ]);
    var _h = useReferenceParams({
        resource: reference,
        // TODO: Introduce real pagination (setters, etc.)
        page: 1,
        perPage: initialPerPageChoices,
        sort: initialSortChoices,
        debounce: debounce,
        filter: initialFilterChoices,
    }), paramsEndResource = _h[0], paramsEndResourceModifiers = _h[1];
    // Load all possible references
    var possibleValues = useGetList(reference, {
        pagination: {
            page: paramsEndResource.page,
            perPage: paramsEndResource.perPage,
        },
        sort: {
            field: paramsEndResource.sort,
            order: paramsEndResource.order,
        },
        filter: paramsEndResource.filter,
    }, {
        keepPreviousData: true,
    });
    var currentValue = useWatch({
        name: temporaryFieldName,
    });
    useEffect(function () {
        if (!possibleValues.isLoading &&
            (currentValue || []).length > 0 &&
            (currentValue || []).some(function (id) { return !possibleValues.data.some(function (record) { return record.id === id; }); })) {
            possibleValues.refetch();
        }
    }, [(currentValue || []).join(), possibleValues.refetch]); // eslint-disable-line
    // Ensure the current references are loaded
    var references = useGetMany(reference, { ids: currentValue }, { enabled: !!currentValue });
    var currentSort = useMemo(function () { return ({
        field: params.sort,
        order: params.order,
    }); }, [params.sort, params.order]);
    var allChoices = useMemo(function () {
        var _a, _b;
        var choices = __spreadArray([], ((_a = possibleValues === null || possibleValues === void 0 ? void 0 : possibleValues.data) !== null && _a !== void 0 ? _a : []), true);
        // Only add the selected records if they are not already in the choices
        ((_b = references === null || references === void 0 ? void 0 : references.data) !== null && _b !== void 0 ? _b : []).forEach(function (record) {
            if (choices.find(function (choice) { return choice.id === record.id; }) === undefined) {
                choices.push(record);
            }
        });
        var sorted = sortBy(choices, paramsEndResource.sort);
        if (paramsEndResource.order === 'DESC') {
            return sorted.reverse();
        }
        return sorted;
    }, [
        possibleValues.data,
        references.data,
        paramsEndResource.sort,
        paramsEndResource.order,
    ]);
    return {
        allChoices: allChoices,
        availableChoices: possibleValues.data,
        displayedFilters: paramsEndResource.displayedFilters,
        error: throughManyReferences.error ||
            references.error ||
            possibleValues.error,
        filterValues: paramsEndResource.filterValues,
        hasNextPage: throughManyReferences.pageInfo
            ? throughManyReferences.pageInfo.hasNextPage
            : throughManyReferences.total != null
                ? params.page * params.perPage < throughManyReferences.total
                : undefined,
        hasPreviousPage: throughManyReferences.pageInfo
            ? throughManyReferences.pageInfo.hasPreviousPage
            : params.page > 1,
        hideFilter: paramsEndResourceModifiers.hideFilter,
        isFetching: throughManyReferences.isFetching ||
            references.isFetching ||
            possibleValues.isFetching,
        isLoading: throughManyReferences.isLoading ||
            references.isLoading ||
            possibleValues.isLoading,
        page: paramsEndResource.page,
        perPage: paramsEndResource.perPage,
        refetch: throughManyReferences.refetch,
        resource: reference,
        selectedChoices: references.data,
        setFilters: paramsEndResourceModifiers.setFilters,
        setPage: paramsEndResourceModifiers.setPage,
        setPerPage: paramsEndResourceModifiers.setPerPage,
        setSort: paramsEndResourceModifiers.setSort,
        showFilter: paramsEndResourceModifiers.showFilter,
        source: temporaryFieldName,
        sort: currentSort,
        total: throughManyReferences.total,
        isFromReference: true,
    };
};
